// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { enumerateInputs } from "./devices";
import { BackgroundEffect, type BgMode } from "./bgeffect";
import { decode, encode } from "./whiteboard/protocol";
import { WHITEBOARD_DC_ID, WHITEBOARD_DC_LABEL, stampAuthor } from "./whiteboard/transport";
import type { WireOp } from "./whiteboard/types";

// CallSession manages one 1:1 WebRTC call (audio, optionally video). Signalling (SDP
// offer/answer + ICE) rides the /sync socket as opaque ephemeral frames (sendSignal);
// media flows P2P, traversing NAT via the homeserver's self-hosted ICE servers
// (STUN/TURN from /config). It is a small state machine: idle → ringing/incoming →
// connecting → connected → ended.
//
// Multi-device: a user may be signed in on several devices, all of which receive an
// incoming invite (and ring). Each CallSession has a per-device id stamped on every
// frame so a call answered on one device is dropped on the others ("taken elsewhere"),
// and a frame from a sibling/stale device never tears down the active call.

export type CallState = "idle" | "ringing" | "incoming" | "connecting" | "connected" | "ended";

// CallQuality is the live link-health summary surfaced in the call UI.
export interface CallQuality {
  state: "good" | "fair" | "poor" | "reconnecting";
  loss?: number; // inbound packet loss, %
  rtt?: number; // round-trip time, ms
}

// pcQuality grades an RTCPeerConnection's live link health from getStats:
// "reconnecting" while it's (re)establishing, else good/fair/poor on inbound packet
// loss + round-trip time. Shared by 1:1 and group calls.
export async function pcQuality(pc: RTCPeerConnection | null): Promise<CallQuality> {
  if (!pc) return { state: "reconnecting" };
  const cs = pc.connectionState;
  if (cs === "disconnected" || cs === "failed" || cs === "connecting" || cs === "new") return { state: "reconnecting" };
  const stats = await pc.getStats();
  type Stat = { type: string; isRemote?: boolean; packetsReceived?: number; packetsLost?: number; nominated?: boolean; selected?: boolean; currentRoundTripTime?: number };
  let received = 0;
  let lost = 0;
  let rtt = 0;
  stats.forEach((r: Stat) => {
    if (r.type === "inbound-rtp" && !r.isRemote) {
      received += r.packetsReceived ?? 0;
      lost += r.packetsLost ?? 0;
    }
    if (r.type === "candidate-pair" && (r.nominated || r.selected) && r.currentRoundTripTime != null) {
      rtt = r.currentRoundTripTime * 1000;
    }
  });
  const loss = received + lost > 0 ? (lost / (received + lost)) * 100 : 0;
  let state: CallQuality["state"] = "good";
  if (loss > 5 || rtt > 400) state = "poor";
  else if (loss > 1.5 || rtt > 200) state = "fair";
  return { state, loss, rtt };
}

// Outgoing-video quality cap. "auto" lifts the cap so the browser's congestion control
// adapts freely to the link; the rest trade resolution/bitrate for bandwidth (e.g. to save
// mobile data). The browser still adapts DOWN within whatever cap is set.
export type VideoQuality = "auto" | "high" | "medium" | "low";
export function applyQualityPreset(e: RTCRtpEncodingParameters, q: VideoQuality): void {
  const presets: Record<VideoQuality, [number | undefined, number]> = {
    auto: [undefined, 1],
    high: [2_000_000, 1],
    medium: [700_000, 1.5],
    low: [200_000, 3],
  };
  const [bitrate, scale] = presets[q];
  if (bitrate === undefined) delete e.maxBitrate;
  else e.maxBitrate = bitrate;
  e.scaleResolutionDownBy = scale;
}

type IceServer = { urls: string[]; username?: string; credential?: string };

interface SignalContent {
  call_id: string;
  kind: "invite" | "answer" | "candidates" | "hangup" | "offer" | "call_state" | "transfer" | "escalate";
  sdp?: string;
  candidates?: RTCIceCandidateInit[];
  video?: boolean; // the invite/offer hints whether the sender is adding video
  device_id?: string; // sender's per-device id (multi-device call coordination)
  peer?: string; // call_state: who the active device is talking to
  muted?: boolean; // call_state: current mic state (so a takeover device matches)
  camera_off?: boolean; // call_state: current camera state
  room?: string; // escalate: the ephemeral group-call room to migrate INTO (not this DM)
}

// TransferInfo describes a live call running on ANOTHER of your devices that this
// device could take over (Messenger-style call transfer). Populated from the active
// device's call_state heartbeat.
export interface TransferInfo {
  callID: string;
  roomID: string;
  peer: string;
  video: boolean;
  muted: boolean;
  cameraOff: boolean;
}

export interface CallEvents {
  onState: (state: CallState) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onLocalStream: (stream: MediaStream) => void; // our own camera/mic for the self-view
  onIncoming: (roomID: string, peerID: string, video: boolean) => void;
  // onTransferable fires when a live call appears on / disappears from another of the
  // user's devices (null = none) — drives the "Move call here" banner.
  onTransferable?: (info: TransferInfo | null) => void;
  // onPeerTransfer fires (on the OTHER party R) when the peer moves the call to another
  // of their devices: migrate this leg to the SFU. onTransferredAway fires on the device
  // LOSING the call (it's being taken over by another of MY devices).
  onPeerTransfer?: (roomID: string, video: boolean) => void;
  onTransferredAway?: () => void;
  // onEscalate fires (on the OTHER party) when the peer adds someone to the call: the 1:1
  // is becoming a group call in the same room, so migrate this leg to the room group call.
  onEscalate?: (roomID: string, video: boolean) => void;
  // onScreenShare fires when WE start/stop screen-sharing (incl. the browser's "Stop
  // sharing") — so the UI updates and the presence signal is sent.
  onScreenShare?: (sharing: boolean) => void;
  // onNoAnswer fires when an outgoing call rings out unanswered (the ring timeout) — so the
  // caller can show "No answer". The call is torn down right after.
  onNoAnswer?: () => void;
  // onCallRecord fires (on the CALLER only) when a 1:1 call REALLY ends — so a Messenger-style
  // record can be posted to the room timeline. Not fired for an escalation/transfer.
  onCallRecord?: (rec: { roomID: string; outcome: "answered" | "missed"; durationMs: number; video: boolean }) => void;
  // onWhiteboardOp fires for a whiteboard op received over the P2P data channel. `author`
  // is already re-stamped to the call peer (anti-spoof) — never trust the payload. The
  // op may be a batch/control op; the consumer (Phase 2/3) routes it to the engine.
  onWhiteboardOp?: (op: WireOp) => void;
  // onWhiteboardChannelState fires when the whiteboard data channel opens/closes, so the
  // UI knows whether live strokes can flow P2P right now.
  onWhiteboardChannelState?: (open: boolean) => void;
}

function newId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export class CallSession {
  private pc: RTCPeerConnection | null = null;
  private local: MediaStream | null = null;
  private roomID = "";
  private peerID = "";
  private peerDeviceID = ""; // the specific peer DEVICE we're bound to (multi-device)
  private callID = "";
  private incomingOffer = "";
  private pending: RTCIceCandidateInit[] = [];
  private st: CallState = "idle";
  private video = false; // is THIS a video call (either side sends video)
  private incomingVideo = false; // did the incoming invite request video
  private isCaller = false; // we placed the call (drives ICE-restart ownership)
  private offerSdp = ""; // our outgoing offer, re-sent while ringing
  private sentCandidates: RTCIceCandidateInit[] = []; // our ICE, re-sent when answered
  private restarts = 0; // ICE-restart attempts this call
  private whiteboardDC: RTCDataChannel | null = null; // negotiated P2P channel for whiteboard ops
  private screenTrack: MediaStreamTrack | null = null; // active screen-share track (in the video sender)
  private savedCamera: MediaStreamTrack | null = null; // camera set aside while screen-sharing, restored on stop
  private quality: VideoQuality = "auto"; // outgoing-video cap, re-applied after track changes
  private bgEffect: BackgroundEffect | null = null; // client-side background blur processor
  private bgMode: BgMode = "none";
  private rawCamera: MediaStreamTrack | null = null; // raw camera while the bg effect is on
  private ringTimer: ReturnType<typeof setInterval> | null = null;
  private ringTimeout: ReturnType<typeof setTimeout> | null = null;
  private dropTimer: ReturnType<typeof setTimeout> | null = null; // grace before tearing down a flaky link
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null; // broadcasts call_state while connected
  private siblingCall: TransferInfo | null = null; // a live call on another of my devices
  private siblingExpiry: ReturnType<typeof setTimeout> | null = null; // clears siblingCall if its heartbeat stops
  // Call-record tracking (Messenger-style timeline entry): when the call connects we stamp
  // the start time; on a REAL end the CALLER posts a record. `recordable` is cleared for an
  // escalation/transfer (the call moves on — no "ended" record).
  private startedAt = 0;
  private answered = false;
  private recordable = true;
  // A stable per-device id: distinguishes THIS device's frames from a sibling device
  // of the same user signed in elsewhere.
  private readonly deviceID = newId();

  constructor(
    private readonly sendSignal: (roomID: string, content: SignalContent) => void,
    private readonly getIceServers: () => Promise<IceServer[]>,
    private readonly myUserID: string,
    private readonly events: CallEvents,
  ) {}

  get state(): CallState {
    return this.st;
  }
  get peer(): string {
    return this.peerID;
  }
  get isVideo(): boolean {
    return this.video;
  }
  get incomingIsVideo(): boolean {
    return this.incomingVideo;
  }
  get sendingVideo(): boolean {
    return !!this.local?.getVideoTracks().length;
  }
  // deviceId is this CallSession's per-device id (used to stamp a transfer trigger).
  get deviceId(): string {
    return this.deviceID;
  }
  // roomId is the room this call lives in (for the screen-share presence signal).
  get roomId(): string {
    return this.roomID;
  }

  // connectionType reports whether the active media path is "direct" (P2P — a host or
  // server-reflexive candidate pair) or "relay" (via TURN/TURNS), from the selected ICE
  // candidate pair. "" = not yet determined. Cheap; safe to poll while connected.
  async connectionType(): Promise<"direct" | "relay" | ""> {
    if (!this.pc) return "";
    const stats = await this.pc.getStats();
    type Stat = { type: string; id: string; state?: string; nominated?: boolean; selected?: boolean; localCandidateId?: string; remoteCandidateId?: string; candidateType?: string };
    const get = (id?: string) => (id ? (stats.get(id) as Stat | undefined) : undefined);
    let pair: Stat | undefined;
    stats.forEach((r: Stat) => {
      if (r.type !== "candidate-pair" || r.state !== "succeeded") return;
      if (r.nominated || r.selected || !pair) pair = r; // prefer the nominated/selected pair
    });
    if (!pair) return "";
    const ends = [get(pair.localCandidateId)?.candidateType, get(pair.remoteCandidateId)?.candidateType];
    if (ends.includes("relay")) return "relay";
    return ends.every(Boolean) ? "direct" : "";
  }

  // verificationSeed returns a stable per-call fingerprint of the ACTUAL negotiated DTLS
  // certificates (local + remote), read off the live connection — NOT from the SDP we were
  // handed (which the homeserver relays and could swap). Sorted so both ends produce the
  // same value. A man-in-the-middle terminating DTLS must present its own cert, so each
  // side would see a different remote fingerprint → a different SAS → the mismatch is
  // audible when the two humans compare emoji. null until DTLS is up.
  async verificationSeed(): Promise<string | null> {
    if (!this.pc) return null;
    const stats = await this.pc.getStats();
    type Stat = { type: string; localCertificateId?: string; remoteCertificateId?: string; fingerprint?: string };
    let transport: Stat | undefined;
    stats.forEach((r: Stat) => {
      if (r.type === "transport") transport = r;
    });
    if (!transport) return null;
    const local = transport.localCertificateId ? (stats.get(transport.localCertificateId) as Stat | undefined) : undefined;
    const remote = transport.remoteCertificateId ? (stats.get(transport.remoteCertificateId) as Stat | undefined) : undefined;
    if (!local?.fingerprint || !remote?.fingerprint) return null;
    return [local.fingerprint, remote.fingerprint].sort((a, b) => a.localeCompare(b)).join("|");
  }

  // linkQuality summarises live link health from getStats: "reconnecting" while the
  // peer connection is re-establishing (a blip the ICE-restart/drop-timer is riding
  // out), otherwise good/fair/poor graded on inbound packet loss + round-trip time.
  // Cheap; safe to poll while connected.
  async linkQuality(): Promise<CallQuality> {
    return pcQuality(this.pc);
  }

  // startCall places an outgoing call to peerID in roomID (video optional).
  async startCall(roomID: string, peerID: string, video = false): Promise<void> {
    if (this.st !== "idle") return;
    this.roomID = roomID;
    this.peerID = peerID;
    this.callID = newId();
    this.video = video;
    this.isCaller = true;
    await this.setupPc(video);
    const offer = await this.pc!.createOffer();
    await this.pc!.setLocalDescription(offer);
    this.offerSdp = offer.sdp ?? "";
    this.setState("ringing");
    this.signal({ call_id: this.callID, kind: "invite", sdp: this.offerSdp, video });
    this.startRinging();
  }

  // startRinging re-sends the invite while ringing, so a callee woken by a push (and
  // reconnecting to /sync after the original ephemeral invite was missed) still gets
  // it. Gives up after a timeout if unanswered.
  private startRinging(): void {
    this.stopRinging();
    this.ringTimer = setInterval(() => {
      if (this.st === "ringing") this.signal({ call_id: this.callID, kind: "invite", sdp: this.offerSdp, video: this.video });
    }, 2500);
    this.ringTimeout = setTimeout(() => {
      if (this.st === "ringing") {
        this.events.onNoAnswer?.();
        this.hangup();
      }
    }, 45_000);
  }
  private stopRinging(): void {
    if (this.ringTimer) clearInterval(this.ringTimer);
    if (this.ringTimeout) clearTimeout(this.ringTimeout);
    this.ringTimer = null;
    this.ringTimeout = null;
  }

  // accept answers a ringing incoming call. Defaults to matching the call's video,
  // but the callee can answer audio-only (video=false) even a video call.
  async accept(video?: boolean): Promise<void> {
    if (this.st !== "incoming") return;
    const sendVideo = video ?? this.incomingVideo; // whether to turn MY camera on
    this.video = this.incomingVideo || sendVideo; // a video call if EITHER side sends video
    await this.setupPc(sendVideo);
    await this.pc!.setRemoteDescription({ type: "offer", sdp: this.incomingOffer });
    for (const c of this.pending) await this.pc!.addIceCandidate(c).catch(() => undefined);
    this.pending = [];
    const answer = await this.pc!.createAnswer();
    await this.pc!.setLocalDescription(answer);
    this.setState("connecting");
    this.signal({ call_id: this.callID, kind: "answer", sdp: answer.sdp });
  }

  // hangup / reject ends the current call (and tells the peer).
  hangup(): void {
    if (this.callID) this.signal({ call_id: this.callID, kind: "hangup" });
    this.cleanup();
  }

  // handOffAndHangup ends the P2P leg WITHOUT stopping the local tracks — they've been
  // handed to another connection (a call transfer's SFU leg now owns them). Detaching
  // the stream first means cleanup() closes the PeerConnection but leaves the tracks live.
  handOffAndHangup(): void {
    this.recordable = false; // a transfer/migration, not a real end — no "ended" record
    this.local = null; // detach: do NOT stop these tracks (the SFU is still using them)
    this.hangup();
  }

  // escalate tells the peer this 1:1 is becoming a group call (someone was added): migrate
  // your leg into `targetRoom` (the ephemeral call room). Sent BEFORE we hand off our leg.
  escalate(targetRoom: string): void {
    if (this.callID) this.signal({ call_id: this.callID, kind: "escalate", video: this.sendingVideo, room: targetRoom });
  }

  // closeForEscalation tears down the P2P leg locally during an escalation WITHOUT sending a
  // hangup. A hangup would make the peer's CallSession cleanup() stop ITS tracks mid-migration
  // (that's what dropped the peer from the call) — the peer tears down its own leg the same
  // way when it processes the `escalate` signal. Tracks are detached, NOT stopped: the group
  // call now owns them.
  closeForEscalation(): void {
    this.recordable = false; // the call moves to the group room — no "ended" record in the DM
    this.local = null;
    this.cleanup();
  }

  // suppressRecord marks this call so its eventual teardown posts NO timeline record —
  // used when the call is being taken over by another of my devices (it didn't really end).
  suppressRecord(): void {
    this.recordable = false;
  }

  // detachLocal releases our local tracks from this P2P leg WITHOUT stopping or closing
  // anything — so if a hangup races in during an escalation, cleanup() won't stop the tracks
  // the group call is about to reuse. Idempotent; safe to call before closeForEscalation.
  detachLocal(): void {
    this.local = null;
  }

  setMuted(muted: boolean): void {
    this.local?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  // setCameraOff toggles the local video track (kept in the call; just not sent).
  setCameraOff(off: boolean): void {
    this.local?.getVideoTracks().forEach((t) => (t.enabled = !off));
  }

  // upgradeToVideo adds a camera to a call in which WE aren't yet sending video, and
  // renegotiates — so either party can turn their camera on mid-call, INCLUDING after
  // answering a video call with audio only (where `this.video` is already true because
  // the peer sends video, so the guard is on sendingVideo, not video).
  async upgradeToVideo(): Promise<void> {
    if (!this.pc || this.sendingVideo || !this.local) return;
    const cam = await navigator.mediaDevices.getUserMedia({ video: true });
    const track = cam.getVideoTracks()[0];
    if (!track) return;
    this.local.addTrack(track);
    this.pc.addTrack(track, this.local);
    this.video = true;
    this.events.onLocalStream(this.local);
    await this.renegotiate();
    await this.applyQuality();
  }

  // renegotiate re-offers after a local track change (added/removed video).
  private async renegotiate(): Promise<void> {
    if (!this.pc) return;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.signal({ call_id: this.callID, kind: "offer", sdp: offer.sdp, video: this.video });
  }

  get isSharingScreen(): boolean {
    return !!this.screenTrack;
  }
  get videoQuality(): VideoQuality {
    return this.quality;
  }
  get backgroundMode(): BgMode {
    return this.bgMode;
  }

  // setBackground turns the client-side background blur on/off. It runs the camera through
  // BackgroundEffect and sends the COMPOSITED track (E2E-safe — peers never see the raw
  // background), restoring the raw camera when turned off.
  async setBackground(mode: BgMode): Promise<void> {
    if (mode === this.bgMode) return;
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === "video");
    if (!sender?.track || !this.local) {
      this.bgMode = mode;
      return;
    }
    if (mode === "none") {
      if (this.rawCamera) {
        await sender.replaceTrack(this.rawCamera);
        this.swapLocalVideo(this.rawCamera);
        this.rawCamera = null;
      }
      this.bgEffect?.stop();
      this.bgMode = "none";
      return;
    }
    if (this.bgEffect && this.rawCamera) {
      this.bgEffect.setMode(mode); // already running — just change the effect
      this.bgMode = mode;
      return;
    }
    this.rawCamera = sender.track;
    this.bgEffect ??= new BackgroundEffect();
    const processed = await this.bgEffect.start(new MediaStream([this.rawCamera]), mode);
    const pTrack = processed.getVideoTracks()[0];
    if (!pTrack) return;
    await sender.replaceTrack(pTrack);
    this.swapLocalVideo(pTrack);
    await this.applyQuality();
    this.bgMode = mode;
  }
  // swapLocalVideo points our self-view at `track` (the raw camera stays alive for the
  // effect's input even after it's removed from the local stream).
  private swapLocalVideo(track: MediaStreamTrack): void {
    if (!this.local) return;
    this.local.getVideoTracks().forEach((t) => this.local?.removeTrack(t));
    this.local.addTrack(track);
    this.events.onLocalStream(this.local);
  }

  // setVideoQuality caps the outgoing video (re-applied automatically after track changes).
  async setVideoQuality(q: VideoQuality): Promise<void> {
    this.quality = q;
    await this.applyQuality();
  }
  private async applyQuality(): Promise<void> {
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === "video");
    if (!sender) return;
    const params = sender.getParameters();
    if (!params.encodings?.length) params.encodings = [{}];
    applyQualityPreset(params.encodings[0], this.quality);
    await sender.setParameters(params).catch(() => undefined);
  }

  // shareScreen captures the display and sends it as our video: a video call swaps the
  // camera for the screen (replaceTrack — no renegotiation); an audio call adds the screen
  // as a new video track (renegotiated). The browser's "Stop sharing" auto-reverts.
  async shareScreen(): Promise<boolean> {
    if (!this.pc || !this.local || this.screenTrack) return false;
    const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
    const screen = display.getVideoTracks()[0];
    if (!screen) return false;
    this.screenTrack = screen;
    screen.onended = () => void this.stopScreen(); // user hit the browser's "Stop sharing"
    const sender = this.pc.getSenders().find((s) => s.track?.kind === "video");
    if (sender?.track) {
      this.savedCamera = sender.track;
      await sender.replaceTrack(screen);
      this.local.removeTrack(this.savedCamera);
    } else {
      this.pc.addTrack(screen, this.local);
      this.video = true;
    }
    this.local.addTrack(screen);
    this.events.onLocalStream(this.local);
    if (!sender?.track) await this.renegotiate();
    await this.applyQuality();
    this.events.onScreenShare?.(true);
    return true;
  }

  // stopScreen reverts a screen share: restore the camera (replaceTrack) or remove the
  // added video track (renegotiate), and stop the display capture.
  async stopScreen(): Promise<void> {
    const screen = this.screenTrack;
    if (!this.pc || !this.local || !screen) return;
    this.screenTrack = null;
    const sender = this.pc.getSenders().find((s) => s.track === screen);
    this.local.removeTrack(screen);
    screen.stop();
    if (this.savedCamera && sender) {
      await sender.replaceTrack(this.savedCamera);
      this.local.addTrack(this.savedCamera);
      this.savedCamera = null;
    } else if (sender) {
      this.pc.removeTrack(sender);
      this.video = false;
      await this.renegotiate();
    }
    this.events.onLocalStream(this.local);
    this.events.onScreenShare?.(false);
  }

  // onSignal routes an inbound signalling frame (from the /sync socket).
  async onSignal(roomID: string, from: string, raw: unknown): Promise<void> {
    const c = raw as SignalContent;
    if (!c || typeof c.kind !== "string") return;
    const dev = c.device_id ?? "";

    // Frames from MY OWN user (I'm signed in on several devices): ignore my own echo,
    // but react to a SIBLING device so the call follows whichever device answers.
    if (from === this.myUserID) {
      if (dev === this.deviceID) return; // my own frame echoed back
      // A sibling ANSWERED or DECLINED the call I'm ringing for → it was handled elsewhere;
      // stop ringing here too WITHOUT signalling a hangup myself (redundant — the device that
      // acted already told the caller). This stops every other device of mine from ringing on.
      if ((c.kind === "answer" || c.kind === "hangup") && this.st === "incoming" && c.call_id === this.callID) this.cleanup();
      else if (c.kind === "call_state") this.noteSiblingCall(roomID, c); // a live call on my other device
      else if (c.kind === "transfer" && c.call_id === this.callID) this.events.onTransferredAway?.(); // my other device is taking THIS call
      else if (c.kind === "hangup") this.clearSiblingCall(c.call_id); // a sibling's OTHER (live) call ended
      return; // never otherwise act on a sibling's invite/offer/candidates
    }

    // The peer is moving the call to another of THEIR devices → migrate to the SFU.
    // (Handled before the per-device gate below, since it comes from a new peer device.)
    if (c.kind === "transfer" && c.call_id === this.callID && from === this.peerID) {
      this.events.onPeerTransfer?.(roomID, !!c.video);
      return;
    }
    // The peer added someone → migrate this leg into the ephemeral group-call room they
    // spun up (carried in the signal; NOT this DM, which stays a private 1:1).
    if (c.kind === "escalate" && c.call_id === this.callID && from === this.peerID) {
      this.events.onEscalate?.(c.room ?? roomID, !!c.video);
      return;
    }

    // From the peer (the other user). Once bound to a specific peer DEVICE (the one
    // that answered / the one calling us), ignore frames from any OTHER device of
    // theirs — e.g. a stale sibling device hanging up must not drop the live call.
    if (this.peerDeviceID && dev && dev !== this.peerDeviceID) return;

    switch (c.kind) {
      case "invite":
        if (this.callID && c.call_id === this.callID) return; // a re-ring of the call we're on
        if (this.st !== "idle") {
          // Busy with a DIFFERENT call — politely decline so that caller stops ringing.
          this.sendSignal(roomID, { call_id: c.call_id, kind: "hangup", device_id: this.deviceID });
          return;
        }
        this.roomID = roomID;
        this.peerID = from;
        this.peerDeviceID = dev; // bind to the calling device
        this.callID = c.call_id;
        this.incomingOffer = c.sdp ?? "";
        this.incomingVideo = !!c.video;
        this.setState("incoming");
        this.events.onIncoming(roomID, from, this.incomingVideo);
        return;
      case "answer":
        if (c.call_id !== this.callID || !this.pc) return;
        this.peerDeviceID = dev || this.peerDeviceID; // bind to the device that answered
        await this.pc.setRemoteDescription({ type: "answer", sdp: c.sdp });
        this.stopRinging();
        // Re-send our ICE — a callee woken by a push missed the candidates we sent
        // while they were offline; without this the connection never completes.
        if (this.sentCandidates.length) this.signal({ call_id: this.callID, kind: "candidates", candidates: this.sentCandidates });
        if (this.st !== "connected") this.setState("connecting");
        return;
      case "offer": {
        // A mid-call renegotiation: the peer turned their camera on, or an ICE restart.
        if (c.call_id !== this.callID || !this.pc) return;
        await this.pc.setRemoteDescription({ type: "offer", sdp: c.sdp });
        if (c.video) this.video = true; // it's now a video call (we receive their video)
        const ans = await this.pc.createAnswer();
        await this.pc.setLocalDescription(ans);
        this.setState(this.st); // re-emit so the UI picks up isVideo
        this.signal({ call_id: this.callID, kind: "answer", sdp: ans.sdp });
        return;
      }
      case "candidates":
        if (c.call_id !== this.callID) return;
        for (const cand of c.candidates ?? []) {
          if (this.pc?.remoteDescription) await this.pc.addIceCandidate(cand).catch(() => undefined);
          else this.pending.push(cand);
        }
        return;
      case "hangup":
        if (c.call_id !== this.callID) return;
        this.cleanup();
        return;
    }
  }

  // devices lists the available microphones + cameras (deduped — see enumerateInputs).
  devices(): Promise<{ mics: MediaDeviceInfo[]; cameras: MediaDeviceInfo[] }> {
    return enumerateInputs();
  }

  setAudioInput(deviceId: string): Promise<void> {
    return this.switchTrack("audio", { deviceId: { exact: deviceId } });
  }
  setVideoInput(deviceId: string): Promise<void> {
    return this.switchTrack("video", { deviceId: { exact: deviceId } });
  }
  private facing: "user" | "environment" = "user";
  // flipCamera toggles front↔back by facingMode (not a specific deviceId): the OS picks the
  // right physical lens, so it works even though phones list auxiliary lenses that can't be
  // opened for capture. Throws if the target facing has no usable camera (caller reverts).
  async flipCamera(): Promise<void> {
    const sender = this.pc?.getSenders().find((s) => s.track?.kind === "video");
    const current = (sender?.track?.getSettings().facingMode as "user" | "environment" | undefined) ?? this.facing;
    const next: "user" | "environment" = current === "environment" ? "user" : "environment";
    await this.switchTrack("video", { facingMode: { exact: next } });
    this.facing = next;
  }

  // switchTrack swaps the sent track for one from another device WITHOUT renegotiation
  // (sender.replaceTrack), preserving the muted/camera-off state, and updates the
  // local stream so the self-view follows. For video on an audio call it upgrades.
  // A broken camera (getUserMedia rejects) throws BEFORE touching the live track, so
  // the call keeps running and the caller surfaces the error.
  private async switchTrack(kind: "audio" | "video", constraint: MediaTrackConstraints): Promise<void> {
    if (!this.pc || !this.local) return;
    const oldTracks = kind === "audio" ? this.local.getAudioTracks() : this.local.getVideoTracks();
    const wasEnabled = oldTracks[0]?.enabled ?? true;
    const stream = await navigator.mediaDevices.getUserMedia(kind === "audio" ? { audio: constraint } : { video: constraint });
    const track = stream.getTracks()[0];
    if (!track) return;
    // Background effect on + switching CAMERA: feed the new camera to the effect (the sent
    // processed track is unchanged), so the blur survives the device switch.
    if (kind === "video" && this.bgEffect && this.rawCamera) {
      await this.bgEffect.setInput(new MediaStream([track]));
      this.rawCamera.stop();
      this.rawCamera = track;
      return;
    }
    track.enabled = wasEnabled;
    const sender = this.pc.getSenders().find((s) => s.track?.kind === kind);
    if (sender) await sender.replaceTrack(track);
    else {
      this.pc.addTrack(track, this.local); // first video track on an audio call → renegotiate
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      this.signal({ call_id: this.callID, kind: "offer", sdp: offer.sdp, video: true });
    }
    for (const t of oldTracks) {
      this.local.removeTrack(t);
      t.stop();
    }
    this.local.addTrack(track);
    if (kind === "video") this.video = true;
    this.events.onLocalStream(this.local);
  }

  private async setupPc(video: boolean): Promise<void> {
    // getUserMedia only exists in a secure context (HTTPS or localhost); on plain
    // http the browser hides it entirely and never prompts.
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(globalThis.isSecureContext ? "no-mic" : "insecure-context");
    }
    const iceServers = await this.getIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    this.local = await navigator.mediaDevices.getUserMedia({ audio: true, video });
    this.events.onLocalStream(this.local);
    for (const track of this.local.getTracks()) pc.addTrack(track, this.local);
    pc.ontrack = (e) => this.events.onRemoteStream(e.streams[0]);
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const cand = e.candidate.toJSON();
      this.sentCandidates.push(cand); // buffered so we can re-send to a woken callee
      this.signal({ call_id: this.callID, kind: "candidates", candidates: [cand] });
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") {
        this.clearDropTimer();
        this.restarts = 0;
        if (!this.startedAt) {
          this.startedAt = Date.now(); // first connect → the call was answered
          this.answered = true;
        }
        this.setState("connected");
      } else if (s === "failed") {
        // The link broke (or never formed). The CALLER drives an ICE restart; the
        // callee waits for the new offer. Don't tear down immediately.
        if (this.isCaller) void this.tryIceRestart();
        else this.armDropTimer();
      } else if (s === "disconnected") {
        // Usually a transient network blip that recovers on its own — give it a grace
        // window rather than dropping a long call over a momentary hiccup.
        this.armDropTimer();
      } else if (s === "closed") {
        this.cleanup();
      }
    };
    this.setupWhiteboardChannel(pc);
    this.pc = pc;
  }

  // setupWhiteboardChannel opens a NEGOTIATED data channel (both peers use the same
  // fixed id, so no extra signalling) carrying whiteboard ops P2P — strokes never
  // touch the homeserver (E2E by construction). Inbound ops are author-stamped to the
  // call peer (anti-spoof) before reaching the consumer.
  private setupWhiteboardChannel(pc: RTCPeerConnection): void {
    const dc = pc.createDataChannel(WHITEBOARD_DC_LABEL, { negotiated: true, id: WHITEBOARD_DC_ID });
    dc.onopen = () => this.events.onWhiteboardChannelState?.(true);
    dc.onclose = () => this.events.onWhiteboardChannelState?.(false);
    dc.onmessage = (e) => {
      if (typeof e.data !== "string") return;
      const op = decode(e.data);
      if (!op) return; // unknown version / malformed → drop
      this.events.onWhiteboardOp?.(stampAuthor(op, this.peerID));
    };
    this.whiteboardDC = dc;
  }

  // sendOp ships a whiteboard op to the call peer over the data channel. A no-op
  // until the channel is open (the engine buffers/late-join sync covers the gap).
  sendOp(op: WireOp): void {
    if (this.whiteboardDC?.readyState === "open") this.whiteboardDC.send(encode(op));
  }

  // whiteboardReady reports whether the P2P whiteboard channel is open right now.
  get whiteboardReady(): boolean {
    return this.whiteboardDC?.readyState === "open";
  }

  // tryIceRestart re-gathers candidates and re-offers (caller side), recovering a
  // failed/stuck connection without dropping the call. Gives up after a couple of tries.
  private async tryIceRestart(): Promise<void> {
    if (!this.pc || this.restarts >= 2) {
      this.armDropTimer();
      return;
    }
    this.restarts++;
    try {
      const offer = await this.pc.createOffer({ iceRestart: true });
      await this.pc.setLocalDescription(offer);
      this.signal({ call_id: this.callID, kind: "offer", sdp: offer.sdp, video: this.sendingVideo });
    } catch {
      this.armDropTimer();
    }
  }

  // armDropTimer tears the call down only if the link is STILL bad after a grace window.
  private armDropTimer(): void {
    if (this.dropTimer) return;
    this.dropTimer = setTimeout(() => {
      const s = this.pc?.connectionState;
      if (s === "disconnected" || s === "failed") this.cleanup();
    }, 12_000);
  }
  private clearDropTimer(): void {
    if (this.dropTimer) clearTimeout(this.dropTimer);
    this.dropTimer = null;
  }

  // signal sends a frame to the room, stamped with our device id so siblings + the
  // peer can attribute it to THIS device.
  private signal(content: SignalContent): void {
    if (this.roomID) this.sendSignal(this.roomID, { ...content, device_id: this.deviceID });
  }

  private setState(s: CallState): void {
    this.st = s;
    if (s === "connected") this.startHeartbeat();
    else this.stopHeartbeat();
    this.events.onState(s);
  }

  // transferable is a live call on ANOTHER of the user's devices that this (idle) device
  // could take over — null when there's none.
  get transferable(): TransferInfo | null {
    return this.siblingCall;
  }

  // startHeartbeat broadcasts our call state + settings every few seconds while
  // connected, so the user's OTHER devices can offer to take the call over.
  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;
    this.sendCallState();
    this.heartbeatTimer = setInterval(() => this.sendCallState(), 3000);
  }
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }
  private sendCallState(): void {
    if (this.st !== "connected") return;
    this.signal({
      call_id: this.callID,
      kind: "call_state",
      peer: this.peerID,
      video: this.video,
      muted: !(this.local?.getAudioTracks()[0]?.enabled ?? true),
      camera_off: this.sendingVideo && !(this.local?.getVideoTracks()[0]?.enabled ?? true),
    });
  }

  // noteSiblingCall records a live call seen on another of my devices (from its heartbeat)
  // and arms an expiry so the banner clears if the heartbeat stops.
  private noteSiblingCall(roomID: string, c: SignalContent): void {
    if (this.st !== "idle") return; // I'm busy with my own call — not a transfer target
    this.siblingCall = { callID: c.call_id, roomID, peer: c.peer ?? "", video: !!c.video, muted: !!c.muted, cameraOff: !!c.camera_off };
    this.events.onTransferable?.(this.siblingCall);
    if (this.siblingExpiry) clearTimeout(this.siblingExpiry);
    this.siblingExpiry = setTimeout(() => this.clearSiblingCall(c.call_id), 9000);
  }
  private clearSiblingCall(callID: string): void {
    if (this.siblingCall?.callID !== callID) return;
    this.siblingCall = null;
    if (this.siblingExpiry) clearTimeout(this.siblingExpiry);
    this.siblingExpiry = null;
    this.events.onTransferable?.(null);
  }

  private cleanup(): void {
    // Post a call record to the timeline (CALLER only, real end only — not an
    // escalation/transfer). Done first, before the fields below are reset.
    if (this.recordable && this.isCaller && this.roomID) {
      this.events.onCallRecord?.({
        roomID: this.roomID,
        outcome: this.answered ? "answered" : "missed",
        durationMs: this.answered && this.startedAt ? Date.now() - this.startedAt : 0,
        video: this.video,
      });
    }
    this.startedAt = 0;
    this.answered = false;
    this.recordable = true;
    this.stopRinging();
    this.stopHeartbeat();
    this.clearDropTimer();
    if (this.whiteboardDC) {
      // Null handlers before close so a closing channel doesn't call back into a
      // half-torn-down session (teardown discipline, WB-021).
      this.whiteboardDC.onopen = this.whiteboardDC.onclose = this.whiteboardDC.onmessage = null;
      try {
        this.whiteboardDC.close();
      } catch {
        /* already closing with the pc */
      }
      this.whiteboardDC = null;
    }
    if (this.pc) {
      // Null the handlers before close() — close() synchronously fires a "closed"
      // connectionstatechange that would otherwise re-enter cleanup().
      this.pc.ontrack = this.pc.onicecandidate = this.pc.onconnectionstatechange = null;
      this.pc.close();
    }
    this.pc = null;
    this.local?.getTracks().forEach((t) => t.stop());
    this.local = null;
    this.screenTrack?.stop();
    this.screenTrack = null;
    this.savedCamera?.stop();
    this.savedCamera = null;
    this.bgEffect?.dispose(); // close the MediaPipe segmenter (WASM/GPU), not just stop the loop
    this.bgEffect = null;
    this.rawCamera?.stop();
    this.rawCamera = null;
    this.bgMode = "none";
    this.callID = "";
    this.roomID = "";
    this.peerID = "";
    this.peerDeviceID = "";
    this.incomingOffer = "";
    this.offerSdp = "";
    this.video = false;
    this.incomingVideo = false;
    this.isCaller = false;
    this.restarts = 0;
    this.pending = [];
    this.sentCandidates = [];
    this.setState("ended");
    setTimeout(() => {
      if (this.st === "ended") this.setState("idle");
    }, 50);
  }
}
